import { useState } from "react";
import { AppWindow, BarChart3, BrainCircuit, FileChartLine, GraduationCap, ImageIcon, Layers3, Microscope, PanelsTopLeft, Settings2, SlidersHorizontal, Sparkles, PanelLeftOpen } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { chatHistoryItems } from "@/mocks/chatHistory";

const logoSrc = "/logo.png";
const collapsedLogoSrc = "/ScienceOneS.png";

interface NavItem {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  path?: string;
}

interface NavGroup {
  groupLabel?: string;
  items: NavItem[];
}

const navGroups: NavGroup[] = [
  {
    items: [
      { icon: AppWindow, label: "科学对话", path: "/" },
      { icon: FileChartLine, label: "创新评价", path: "/innovation" },
      { icon: Layers3, label: "知识库", path: "/knowledge" },
      { icon: ImageIcon, label: "科研资讯" },
    ],
  },
  {
    groupLabel: "ScienceHub",
    items: [
      { icon: BarChart3, label: "科学数据" },
      { icon: SlidersHorizontal, label: "科学工具" },
      { icon: Microscope, label: "科学实验", path: "/experiments" },
    ],
  },
  {
    groupLabel: "Expert",
    items: [
      { icon: GraduationCap, label: "专家库" },
      { icon: BrainCircuit, label: "ScienceClaw" },
    ],
  },
];

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

export default function Sidebar({ collapsed, onToggle }: SidebarProps) {
  const [searchVisible, setSearchVisible] = useState(false);
  const [historyExpanded, setHistoryExpanded] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [logoHovered, setLogoHovered] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const filteredHistory = chatHistoryItems.filter((item) => item.title.toLowerCase().includes(searchQuery.toLowerCase()));

  return (
    <aside
      className={`flex h-screen shrink-0 flex-col border-r border-border bg-background transition-all duration-200 ${collapsed ? "w-16" : "w-[250px]"}`}
    >
      <div className="flex h-16 items-center justify-between px-4">
        <button
          type="button"
          onClick={() => (collapsed ? onToggle() : undefined)}
          onMouseEnter={() => setLogoHovered(true)}
          onMouseLeave={() => setLogoHovered(false)}
          className="flex items-center no-underline cursor-pointer"
        >
          {collapsed ? (
            logoHovered ? (
              <PanelLeftOpen className="h-[28px] w-[28px] text-foreground" />
            ) : (
              <img
                src={collapsedLogoSrc}
                alt="ScienceOne"
                className="h-[28px] w-[28px] rounded-none object-cover shrink-0"
              />
            )
          ) : (
            <img
              src={logoSrc}
              alt="ScienceOne"
              className="h-[28px] w-auto rounded-none object-contain shrink-0"
            />
          )}
        </button>

        {!collapsed && (
          <button onClick={onToggle} className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground">
            <i className="ri-layout-right-line text-base" />
          </button>
        )}
      </div>

      <nav className="min-h-0 flex-1 overflow-y-auto px-3 py-3">
        {navGroups.map((group, gi) => (
          <div key={gi} className={gi > 0 ? "mt-2" : ""}>
            {group.groupLabel && !collapsed && <div className="px-3 pb-2 pt-3 text-xs font-medium text-muted-foreground">{group.groupLabel}</div>}
            {group.groupLabel && collapsed && <div className="my-2 border-t border-border" />}
            {group.items.map((item) => {
              const isActive = item.path
                ? item.path === "/"
                  ? location.pathname === "/"
                  : location.pathname === item.path || location.pathname.startsWith(`${item.path}/`)
                : false;
              const Icon = item.icon;
              return (
                <button
                  key={item.label}
                  onClick={() => item.path && navigate(item.path)}
                  className={`mb-1 flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors ${item.path ? "cursor-pointer" : "cursor-default"} ${collapsed ? "justify-center" : ""} ${
                    isActive ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                  }`}
                  title={collapsed ? item.label : undefined}
                >
                  <Icon className="h-4 w-4" />
                  {!collapsed && <span>{item.label}</span>}
                </button>
              );
            })}
          </div>
        ))}

        {!collapsed && (
          <div className="mt-4 space-y-3">
            <div className="flex items-center justify-between gap-2 px-1">
              <button
                onClick={() => setHistoryExpanded(!historyExpanded)}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
              >
                历史对话
                <PanelsTopLeft className={`h-3.5 w-3.5 transition-transform ${historyExpanded ? "rotate-0" : "-rotate-90"}`} />
              </button>
              <button
                onClick={() => setSearchVisible(!searchVisible)}
                className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
              >
                <Sparkles className="h-3.5 w-3.5" />
              </button>
            </div>

            {searchVisible && (
              <div>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="搜索对话..."
                  className="h-10 w-full rounded-lg border border-input bg-background px-3 text-sm outline-none placeholder:text-muted-foreground focus:ring-2 focus:ring-ring"
                />
              </div>
            )}

            {historyExpanded && (
              <div className="space-y-1">
                {filteredHistory.map((item) => (
                  <button
                    key={item.id}
                    className="w-full truncate rounded-lg px-3 py-2 text-left text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
                  >
                    {item.title}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </nav>

      <div className="border-t border-border px-4 py-2">
        {collapsed ? (
          <div className="flex justify-center py-1">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-sm font-semibold text-primary-foreground">P</div>
          </div>
        ) : (
          <div className="flex items-center gap-3 py-1.5">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-semibold text-primary-foreground">P</div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium text-foreground">用户名特别长就缩略...</div>
              <div className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
                <BrainCircuit className="h-3.5 w-3.5 text-amber-500" />
                <span>1,822</span>
                <span className="rounded-full bg-muted px-1.5 py-0.5 font-medium">Free</span>
              </div>
            </div>
            <button className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground">
              <Settings2 className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    </aside>
  );
}
